/* eslint-disable tsdoc/syntax */
import { isSafari } from '../browser-kit';
import { serializerDomToString } from '../file-kit';
import isAvailableComputedStyleKey from './ignore-comuted-style-keys';

interface Options {
    extraCssStrings?: string[];
    /** 转换base64的过程中时, 不将全局的fontFaces加入到base64中, 否则体积可能会非常大 */
    disableWebFonts?: boolean;
    filter?: (node: Node) => boolean;
    bgcolor?: string;
    quality?: number;
    imagePlaceholder?: string;
    cacheBust?: boolean;
}

const util = newUtil();
const inliner = newInliner();
const fontFaces = newFontFaces();
const images = newImages();

const defaultOptions: Options = {
    /** Default is to fail on error, no placeholder */
    imagePlaceholder: undefined,
    /** Default cache bust is false, it will use the cache */
    cacheBust: false,
    /** 转换base64的过程中时, 不将全局的fontFaces加入到base64中, 否则体积可能会非常大 */
    disableWebFonts: false,
};

/**
 * 在dom-to-image的基础上:
 * 1. 新增传入自定义css的功能
 * 2. 支持复杂dom的图片生成 (复杂dom生成的base64 url会非常大, 导致canvas生成失败)
 * 源仓库代码: https://github.com/tsayen/dom-to-image/blob/2.6.0/src/dom-to-image.js
 */
interface IDomToImage {
    toCanvas(node: Node, options?: Options): Promise<HTMLCanvasElement>;
    toSvg(node: Node, options?: Options): Promise<string>;
    toPng(node: Node, options?: Options): Promise<string>;
    toJpeg(node: Node, options?: Options): Promise<string>;
    toBlob(node: Node, options?: Options): Promise<Blob>;
    toPixelData(node: Node, options?: Options): Promise<Uint8ClampedArray>;
}

const DomToImage = {
    toSvg: toSvg,
    toCanvas: toCanvas,
    toPng: toPng,
    toJpeg: toJpeg,
    toBlob: toBlob,
    toPixelData: toPixelData,
    impl: {
        fontFaces: fontFaces,
        images: images,
        util: util,
        inliner: inliner,
        options: {},
    },
};

/**
   * @param {Node} node - The DOM Node object to render
   * @param {Object} options - Rendering options
   * @param {Function} options.filter - Should return true if passed node should be included in the output
   *          (excluding node means excluding it's children as well). Not called on the root node.
   * @param {String} options.bgcolor - color for the background, any valid CSS color value.
   * @param {Number} options.quality - a Number between 0 and 1 indicating image quality (applicable to JPEG only),
              defaults to 1.0.
   * @param {String} options.imagePlaceholder - dataURL to use as a placeholder for failed images, default behaviour is to fail fast on images we can't fetch
   * @param {Boolean} options.cacheBust - set to true to cache bust by appending the time to the request url
   * @param {Number} svgScale
   * @return {Promise} - A promise that is fulfilled with a SVG image data URL
   */
function toSvg(node, options, svgScale) {
    options = options || {};
    copyOptions(options);
    return Promise.resolve(node)
        .then(function (node) {
            return cloneNode(node, options.filter, true);
        })
        .then(embedFonts)
        .then(inlineImages)
        .then(applyOptions)
        .then(function (clone) {
            return makeSvgDataUri(clone, util.width(node), util.height(node), svgScale);
        });

    function applyOptions(clone) {
        if (options.bgcolor) clone.style.backgroundColor = options.bgcolor;
        return clone;
    }
}

/**
 * @param {Node} node - The DOM Node object to render
 * @param {Object} options - Rendering options, @see {@link toSvg}
 * @return {Promise} - A promise that is fulfilled with a Uint8Array containing RGBA pixel data.
 * */
function toPixelData(node, options) {
    return draw(node, options || {}).then(function (canvas) {
        return (
            canvas.getContext('2d')?.getImageData(0, 0, util.width(node), util.height(node))?.data ||
            ([] as Uint8ClampedArray[])
        );
    });
}

/**
 * @param {Node} node - The DOM Node object to render
 * @param {Object} options - Rendering options, @see {@link toSvg}
 * @return {Promise<HTMLCanvasElement>} - A promise that is fulfilled with a HTMLCanvasElement
 * */
function toCanvas(node, options) {
    return draw(node, options || {});
}

/**
 * @param {Node} node - The DOM Node object to render
 * @param {Object} options - Rendering options, @see {@link toSvg}
 * @return {Promise} - A promise that is fulfilled with a PNG image data URL
 * */
function toPng(node, options) {
    return draw(node, options || {}).then(function (canvas) {
        return canvas.toDataURL();
    });
}

/**
 * @param {Node} node - The DOM Node object to render
 * @param {Object} options - Rendering options, @see {@link toSvg}
 * @return {Promise} - A promise that is fulfilled with a JPEG image data URL
 * */
function toJpeg(node, options) {
    options = options || {};
    return draw(node, options).then(function (canvas) {
        return canvas.toDataURL('image/jpeg', options.quality || 1.0);
    });
}

/**
 * @param {Node} node - The DOM Node object to render
 * @param {Object} options - Rendering options, @see {@link toSvg}
 * @return {Promise} - A promise that is fulfilled with a PNG image blob
 * */
function toBlob(node, options) {
    return draw(node, options || {}).then(util.canvasToBlob);
}

function copyOptions(options) {
    // Copy options to impl options for use in impl
    if (typeof options.imagePlaceholder === 'undefined') {
        (DomToImage.impl.options as any).imagePlaceholder = defaultOptions.imagePlaceholder;
    } else {
        (DomToImage.impl.options as any).imagePlaceholder = options.imagePlaceholder;
    }

    if (typeof options.cacheBust === 'undefined') {
        (DomToImage.impl.options as any).cacheBust = defaultOptions.cacheBust;
    } else {
        (DomToImage.impl.options as any).cacheBust = options.cacheBust;
    }

    (DomToImage.impl.options as any).extraCssStrings = options.extraCssStrings;
    (DomToImage.impl.options as any).disableWebFonts = options.disableWebFonts;
}

function draw(domNode, options) {
    const domSize = {
        width: util.width(domNode),
        height: util.height(domNode),
    };
    const canvasSize = getCanvasSize(domSize);
    return toSvg(domNode, options, canvasSize.scale)
        .then(util.makeImage)
        .then(util.delay(100))
        .then(function (image: any) {
            const canvas = document.createElement('canvas');
            canvas.width = canvasSize.width;
            canvas.height = canvasSize.height;
            if (options.bgcolor) {
                const ctx = canvas.getContext('2d');
                if (ctx) {
                    ctx.fillStyle = options.bgcolor;
                    ctx.fillRect(0, 0, canvas.width, canvas.height);
                }
            }
            canvas.getContext('2d')?.drawImage(image, 0, 0);
            return canvas;
        });

    /**
     * @param {{ width: number; height: number; }} size
     * @return {{ width: number; height: number; scale: number; }}
     */
    function getCanvasSize(size) {
        /** @typedef {{ uaRegexp: RegExp; maxW: number; maxSize: number; }} LimitInfo */
        /** 搜狗浏览器13000*13000页面会崩溃, 保险起见取10000 * 10000, 其他没测过的浏览器暂时也用这个值 */
        /** @type {Omit<LimitInfo, 'uaRegexp'>} */
        const commonLimit = { maxW: 10000, maxSize: 10000 * 10000 };
        /** @type {LimitInfo[]} */
        const limitList = [
            // chrome
            { uaRegexp: /webkit\W.*(chrome|chromium)\W/i, maxW: 65535, maxSize: 268435456 },
            // edge
            { uaRegexp: /\bEdge\b/i, maxW: 65535, maxSize: 268435456 },
            // safari
            { uaRegexp: /webkit\W(?!.*chrome).*safari\W/i, maxW: 1000000, maxSize: 268435456 },
            // firefox
            { uaRegexp: /mozilla.*\Wfirefox\W/i, maxW: 32767, maxSize: 536756224 },
            // opera (没测过)
            { uaRegexp: /opera.*\Wpresto\W|OPR/i, ...commonLimit },
            // 360 (13000 * 13000会导致页面崩溃)
            { uaRegexp: /360/i, ...commonLimit },
            // uc (没测过)
            { uaRegexp: /ucbrowser/i, ...commonLimit },
            // baidu (没测过)
            { uaRegexp: /bidubrowser/i, ...commonLimit },
            // sougou (13000 * 13000会导致页面崩溃)
            { uaRegexp: /metasr/i, ...commonLimit },
            // liebao (没测过)
            { uaRegexp: /lbbrowser/i, ...commonLimit },
            // qq
            { uaRegexp: /qq/i, maxW: 1000000, maxSize: 268435456 },
        ];
        const limitInfo = limitList.find((l) => l.uaRegexp.test(window.navigator.userAgent)) ?? commonLimit;
        const MAX_W = limitInfo.maxW;
        const MAX_H = limitInfo.maxW;
        const MAX_SIZE = limitInfo.maxSize;
        let width = size.width;
        let height = size.height;
        let scale = 1;
        if (width > MAX_W) {
            const time = MAX_W / width;
            width = MAX_W;
            width = width * time;
            scale = scale * time;
        }
        if (height > MAX_H) {
            const time = MAX_H / height;
            height = MAX_H;
            width = width * time;
            scale = scale * time;
        }
        if (width * height > MAX_SIZE) {
            const time = Math.pow(MAX_SIZE / (width * height), 0.5);
            height = height * time;
            width = width * time;
            scale = scale * time;
        }
        return {
            width,
            height,
            scale,
        };
    }
}

function cloneNode(node: Element, filter, root?: boolean) {
    if (!root && filter && !filter(node)) return Promise.resolve();

    return Promise.resolve(node)
        .then(makeNodeCopy)
        .then(function (clone) {
            return cloneChildren(node, clone, filter);
        })
        .then(function (clone) {
            return processClone(node, clone);
        });

    function makeNodeCopy(node) {
        if (node instanceof HTMLCanvasElement) return util.makeImage(node.toDataURL());
        return node.cloneNode(false);
    }

    function cloneChildren(original, clone, filter) {
        const children = original.childNodes;
        if (children.length === 0) return Promise.resolve(clone);

        return cloneChildrenInOrder(clone, util.asArray(children), filter).then(function () {
            return clone;
        });

        function cloneChildrenInOrder(parent, children, filter) {
            let done = Promise.resolve();
            children.forEach(function (child) {
                done = done
                    .then(function () {
                        return cloneNode(child, filter);
                    })
                    .then(function (childClone) {
                        if (childClone) parent.appendChild(childClone);
                    });
            });
            return done;
        }
    }

    function processClone(original: Element, clone) {
        if (!(clone instanceof Element)) return clone;

        return Promise.resolve()
            .then(cloneStyle)
            .then(clonePseudoElements)
            .then(copyUserInput)
            .then(fixSvg)
            .then(function () {
                return clone;
            });

        function cloneStyle() {
            // 方便排查问题
            const addUsingComputedStyle = (key: string, value: string) => {
                const canDo =
                    window.location.hostname.includes('localhost') ||
                    window.location.hostname.includes('pre') ||
                    window.location.hostname.includes('beta');
                if (!canDo) return;
                if (!globalThis.usingComputedStyle) {
                    // { [key]: string[] }
                    globalThis.usingComputedStyle = {};
                }
                if (!globalThis.usingComputedStyle[key]) {
                    globalThis.usingComputedStyle[key] = [];
                }
                if (globalThis.usingComputedStyle[key].includes(value)) return;
                globalThis.usingComputedStyle[key].push(value);
            };
            copyStyle(original, window.getComputedStyle(original), clone.style);

            function copyStyle(original: Element, source: CSSStyleDeclaration, target: CSSStyleDeclaration) {
                if (source.cssText) {
                    let cssText = source.cssText;
                    // safari部分版本拿到的width为0, 需要手动设置, 否则table布局会不一致
                    if (original.tagName.toLowerCase() === 'col') {
                        const originalWidth = (original as HTMLTableColElement).style.width;
                        const computedWidth = source.getPropertyValue('width');
                        if (originalWidth && (!computedWidth || computedWidth === '0' || computedWidth === '0px')) {
                            cssText += `width:${originalWidth}${source.getPropertyPriority('width') ? ' !important' : ''};`;
                        }
                    }
                    target.cssText = cssText;
                    return;
                }

                // safari下, CSSStyleDeclaration['setProperty']时间特别长, 原因大概是safari下, 就算cloneNode的node没被挂载到document上, CSSStyleDeclaration.setProperty会导致重排, 所以用cssText, 防止每个node都造成多次重排
                // TODO: safari下, 每个node还是会造成一次重排, 如何解决
                if (isSafari) {
                    let cssText = '';
                    util.asArray(source).forEach(function (name) {
                        // 降低base64 url体积, 防止image转成canvas失败
                        if (!isAvailableComputedStyleKey(name)) return;
                        addUsingComputedStyle(name, source.getPropertyValue(name));
                        cssText += `${name}:${source.getPropertyValue(name)}${source.getPropertyPriority(name) ? ' !important' : ''};`;
                    });
                    // safari部分版本拿到的width为0, 需要手动设置, 否则table布局会不一致
                    if (original.tagName.toLowerCase() === 'col') {
                        const originalWidth = (original as HTMLTableColElement).style.width;
                        const computedWidth = source.getPropertyValue('width');
                        if (originalWidth && (!computedWidth || computedWidth === '0' || computedWidth === '0px')) {
                            cssText += `width:${originalWidth}${source.getPropertyPriority('width') ? ' !important' : ''};`;
                        }
                    }
                    target.cssText = cssText;
                } else {
                    util.asArray(source).forEach(function (name) {
                        // 降低base64 url体积, 防止image转成canvas失败
                        if (!isAvailableComputedStyleKey(name)) return;
                        addUsingComputedStyle(name, source.getPropertyValue(name));
                        target.setProperty(name, source.getPropertyValue(name), source.getPropertyPriority(name));
                    });
                }
            }
        }

        function clonePseudoElements() {
            [':before', ':after'].forEach(function (element) {
                clonePseudoElement(element);
            });

            function clonePseudoElement(element: string) {
                const style = window.getComputedStyle(original, element);
                const content = style.getPropertyValue('content');

                if (content === '' || content === 'none') return;

                const className = util.uid();
                clone.className = clone.className + ' ' + className;
                const styleElement = document.createElement('style');
                styleElement.appendChild(formatPseudoElementStyle(className, element, style));
                clone.appendChild(styleElement);

                function formatPseudoElementStyle(className, element, style) {
                    const selector = '.' + className + ':' + element;
                    const cssText = style.cssText ? formatCssText(style) : formatCssProperties(style);
                    return document.createTextNode(selector + '{' + cssText + '}');

                    function formatCssText(style) {
                        const content = style.getPropertyValue('content');
                        return style.cssText + ' content: ' + content + ';';
                    }

                    function formatCssProperties(style) {
                        return util.asArray(style).map(formatProperty).join('; ') + ';';

                        function formatProperty(name) {
                            return (
                                name +
                                ': ' +
                                style.getPropertyValue(name) +
                                (style.getPropertyPriority(name) ? ' !important' : '')
                            );
                        }
                    }
                }
            }
        }

        function copyUserInput() {
            if (original instanceof HTMLTextAreaElement) clone.innerHTML = original.value;
            if (original instanceof HTMLInputElement) clone.setAttribute('value', original.value);
        }

        function fixSvg() {
            if (!(clone instanceof SVGElement)) return;
            clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');

            if (!(clone instanceof SVGRectElement)) return;
            ['width', 'height'].forEach(function (attribute) {
                const value = clone.getAttribute(attribute);
                if (!value) return;

                clone.style.setProperty(attribute, value);
            });
        }
    }
}

function embedFonts(node) {
    return fontFaces.resolveAll().then(function (cssText) {
        const styleNode = document.createElement('style');
        node.appendChild(styleNode);
        styleNode.appendChild(document.createTextNode(cssText));
        return node;
    });
}

function inlineImages(node) {
    return images.inlineAll(node).then(function () {
        return node;
    });
}

function makeSvgDataUri(node, width, height, svgScale) {
    return Promise.resolve(node)
        .then(function (node) {
            node.setAttribute('xmlns', 'http://www.w3.org/1999/xhtml');
            return serializerDomToString(node);
        })
        .then(util.escapeXhtml)
        .then(function (xhtml) {
            return '<foreignObject x="0" y="0" width="100%" height="100%">' + xhtml + '</foreignObject>';
        })
        .then(function (foreignObject) {
            return `<svg\
                        xmlns="http://www.w3.org/2000/svg"\
                        width="${width}"\
                        height="${height}"\
                        style="transform-origin: left top; scale: ${svgScale * 100}%;"
                    >\
                        ${foreignObject}\
                    </svg>`;
        })
        .then(function (svg) {
            console.log('===[dom-to-image] svg size: ', svg.length / 1000 + 'kB');

            // 测试代码
            // console.log('===svg', svg);
            // const div = document.createElement('div');
            // div.id = 'test';
            // div.innerHTML = svg;
            // div.style.position = 'fixed';
            // div.style.top = '0';
            // div.style.left = '0';
            // div.style.zIndex = '10000';
            // div.style.transformOrigin = 'top left';
            // div.style.scale = '0.2';
            // document.body.appendChild(div);

            // 特殊字符会导致svg图片加载失败
            return 'data:image/svg+xml;charset=utf-8,' + svg.replace(/(\v|\f)/g, '');
        });
}

export function newUtil() {
    return {
        escape: escape,
        parseExtension: parseExtension,
        mimeType: mimeType,
        dataAsUrl: dataAsUrl,
        isDataUrl: isDataUrl,
        canvasToBlob: canvasToBlob,
        resolveUrl: resolveUrl,
        getAndEncode: getAndEncode,
        uid: uid(),
        delay: delay,
        asArray: asArray,
        escapeXhtml: escapeXhtml,
        makeImage: makeImage,
        width: width,
        height: height,
    };

    function mimes() {
        /*
         * Only WOFF and EOT mime types for fonts are 'real'
         * see http://www.iana.org/assignments/media-types/media-types.xhtml
         */
        const WOFF = 'application/font-woff';
        const JPEG = 'image/jpeg';

        return {
            woff: WOFF,
            woff2: WOFF,
            ttf: 'application/font-truetype',
            eot: 'application/vnd.ms-fontobject',
            png: 'image/png',
            jpg: JPEG,
            jpeg: JPEG,
            gif: 'image/gif',
            tiff: 'image/tiff',
            svg: 'image/svg+xml',
        };
    }

    function parseExtension(url: string) {
        const match = /\.([^\.\/]*?)$/g.exec(url);
        if (match) return match[1];
        else return '';
    }

    function mimeType(url: string) {
        const extension = parseExtension(url).toLowerCase();
        return mimes()[extension] || '';
    }

    function isDataUrl(url) {
        return url.search(/^(data:)/) !== -1;
    }

    function toBlob(canvas): Promise<Blob> {
        return new Promise(function (resolve) {
            const binaryString = window.atob(canvas.toDataURL().split(',')[1]);
            const length = binaryString.length;
            const binaryArray = new Uint8Array(length);

            for (let i = 0; i < length; i++) binaryArray[i] = binaryString.charCodeAt(i);

            resolve(
                new Blob([binaryArray], {
                    type: 'image/png',
                }),
            );
        });
    }

    function canvasToBlob(canvas: HTMLCanvasElement, type?: string, quality?: number): Promise<Blob> {
        if (canvas.toBlob)
            return new Promise(function (resolve) {
                canvas.toBlob((blob) => resolve(blob!), type, quality);
            });

        return toBlob(canvas);
    }

    function resolveUrl(url: string, baseUrl: string) {
        const doc = document.implementation.createHTMLDocument();
        const base = doc.createElement('base');
        doc.head.appendChild(base);
        const a = doc.createElement('a');
        doc.body.appendChild(a);
        base.href = baseUrl;
        a.href = url;
        return a.href;
    }

    function uid() {
        let index = 0;

        return function () {
            return 'u' + fourRandomChars() + index++;

            function fourRandomChars() {
                /* see http://stackoverflow.com/a/6248722/2519373 */
                return ('0000' + ((Math.random() * Math.pow(36, 4)) << 0).toString(36)).slice(-4);
            }
        };
    }

    function makeImage(uri) {
        return new Promise(function (resolve, reject) {
            const image = new Image();
            image.onload = function () {
                resolve(image);
            };
            image.onerror = (error) => {
                console.log('dom-to-image 图片加载失败:', error, uri);
                reject(error);
            };
            image.src = uri;
        });
    }

    function getAndEncode(url: string) {
        const TIMEOUT = 30000;
        if ((DomToImage.impl.options as any).cacheBust) {
            // Cache bypass so we dont have CORS issues with cached images
            // Source: https://developer.mozilla.org/en/docs/Web/API/XMLHttpRequest/Using_XMLHttpRequest#Bypassing_the_cache
            url += (/\?/.test(url) ? '&' : '?') + new Date().getTime();
        }

        return new Promise(function (resolve) {
            const request = new XMLHttpRequest();

            request.onreadystatechange = done;
            request.ontimeout = timeout;
            request.responseType = 'blob';
            request.timeout = TIMEOUT;
            request.open('GET', url, true);
            request.send();

            let placeholder;
            if ((DomToImage.impl.options as any).imagePlaceholder) {
                const split = (DomToImage.impl.options as any).imagePlaceholder.split(/,/);
                if (split && split[1]) {
                    placeholder = split[1];
                }
            }

            function done() {
                if (request.readyState !== 4) return;

                if (request.status !== 200) {
                    if (placeholder) {
                        resolve(placeholder);
                    } else {
                        fail('cannot fetch resource: ' + url + ', status: ' + request.status);
                    }

                    return;
                }

                const encoder = new FileReader();
                encoder.onloadend = function () {
                    const content = (encoder.result as string)?.split(/,/)[1];
                    resolve(content);
                };
                encoder.readAsDataURL(request.response);
            }

            function timeout() {
                if (placeholder) {
                    resolve(placeholder);
                } else {
                    fail('timeout of ' + TIMEOUT + 'ms occured while fetching resource: ' + url);
                }
            }

            function fail(message) {
                console.error(message);
                resolve('');
            }
        });
    }

    function dataAsUrl(content, type) {
        return 'data:' + type + ';base64,' + content;
    }

    function escape(string) {
        return string.replace(/([.*+?^${}()|\[\]\/\\])/g, '\\$1');
    }

    function delay(ms) {
        return function (arg) {
            return new Promise(function (resolve) {
                setTimeout(function () {
                    resolve(arg);
                }, ms);
            });
        };
    }

    function asArray(arrayLike: any) {
        const array: any[] = [];
        const length = arrayLike.length;
        for (let i = 0; i < length; i++) array.push(arrayLike[i]);
        return array;
    }

    function escapeXhtml(string) {
        return string.replace(/#/g, '%23').replace(/\n/g, '%0A');
    }

    function width(node: any) {
        const leftBorder = px(node, 'border-left-width');
        const rightBorder = px(node, 'border-right-width');
        return node.scrollWidth + leftBorder + rightBorder;
    }

    function height(node: any) {
        const topBorder = px(node, 'border-top-width');
        const bottomBorder = px(node, 'border-bottom-width');
        return node.scrollHeight + topBorder + bottomBorder;
    }

    function px(node: any, styleProperty: string) {
        const value = window.getComputedStyle(node).getPropertyValue(styleProperty);
        return parseFloat(value.replace('px', ''));
    }
}

function newInliner() {
    const URL_REGEX = /url\(['"]?([^'"]+?)['"]?\)/g;

    return {
        inlineAll: inlineAll,
        shouldProcess: shouldProcess,
        impl: {
            readUrls: readUrls,
            inline: inline,
        },
    };

    function shouldProcess(string) {
        return string.search(URL_REGEX) !== -1;
    }

    function readUrls(string) {
        const result: string[] = [];
        let match: RegExpExecArray | null;
        while ((match = URL_REGEX.exec(string)) !== null) {
            result.push(match[1]);
        }
        return result.filter(function (url) {
            return !util.isDataUrl(url);
        });
    }

    function inline(string, url, baseUrl, get) {
        return Promise.resolve(url)
            .then(function (url) {
                return baseUrl ? util.resolveUrl(url, baseUrl) : url;
            })
            .then(get || util.getAndEncode)
            .then(function (data) {
                return util.dataAsUrl(data, util.mimeType(url));
            })
            .then(function (dataUrl) {
                return string.replace(urlAsRegex(url), '$1' + dataUrl + '$3');
            });

        function urlAsRegex(url) {
            return new RegExp('(url\\([\'"]?)(' + util.escape(url) + ')([\'"]?\\))', 'g');
        }
    }

    function inlineAll(string, baseUrl?: any, get?: any) {
        if (nothingToInline()) return Promise.resolve(string);

        return Promise.resolve(string)
            .then(readUrls)
            .then(function (urls) {
                let done = Promise.resolve(string);
                urls.forEach(function (url) {
                    done = done.then(function (string) {
                        return inline(string, url, baseUrl, get);
                    });
                });
                return done;
            });

        function nothingToInline() {
            return !shouldProcess(string);
        }
    }
}

function newFontFaces() {
    return {
        resolveAll: resolveAll,
        impl: {
            readAll: readAll,
        },
    };

    function resolveAll() {
        const extraCssText = (DomToImage.impl.options as any).extraCssStrings?.join('') ?? '';
        if ((DomToImage.impl.options as any).disableWebFonts) {
            return Promise.resolve(extraCssText);
        }
        return readAll()
            .then(function (webFonts) {
                return Promise.all(
                    webFonts.map(function (webFont) {
                        return webFont.resolve();
                    }),
                );
            })
            .then(function (cssStrings) {
                return cssStrings.join('') + extraCssText;
            });
    }

    function readAll() {
        return Promise.resolve(util.asArray(document.styleSheets))
            .then(getCssRules)
            .then(selectWebFontRules)
            .then(function (rules) {
                return rules.map(newWebFont);
            });

        function selectWebFontRules(cssRules) {
            return cssRules
                .filter(function (rule) {
                    return rule.type === CSSRule.FONT_FACE_RULE;
                })
                .filter(function (rule) {
                    return inliner.shouldProcess(rule.style.getPropertyValue('src'));
                });
        }

        function getCssRules(styleSheets: any) {
            const cssRules: any[] = [];
            styleSheets.forEach(function (sheet: any) {
                try {
                    util.asArray(sheet.cssRules || []).forEach(cssRules.push.bind(cssRules));
                } catch (e) {
                    console.log('Error while reading CSS rules from ' + sheet.href, e);
                }
            });
            return cssRules;
        }

        function newWebFont(webFontRule) {
            return {
                resolve: function resolve() {
                    const baseUrl = (webFontRule.parentStyleSheet || {}).href;
                    return inliner.inlineAll(webFontRule.cssText, baseUrl);
                },
                src: function () {
                    return webFontRule.style.getPropertyValue('src');
                },
            };
        }
    }
}

function newImages() {
    return {
        inlineAll: inlineAll,
        impl: {
            newImage: newImage,
        },
    };

    function newImage(element) {
        return {
            inline: inline,
        };

        function inline(get?: any) {
            if (util.isDataUrl(element.src)) return Promise.resolve();

            return Promise.resolve(element.src)
                .then(get || util.getAndEncode)
                .then(function (data) {
                    return util.dataAsUrl(data, util.mimeType(element.src));
                })
                .then(function (dataUrl) {
                    return new Promise(function (resolve, reject) {
                        element.onload = resolve;
                        element.onerror = reject;
                        element.src = dataUrl;
                    });
                });
        }
    }

    function inlineAll(node) {
        if (!(node instanceof Element)) return Promise.resolve(node);

        return inlineBackground(node).then(function () {
            if (node instanceof HTMLImageElement) return newImage(node).inline();
            else
                return Promise.all(
                    util.asArray(node.childNodes).map(function (child) {
                        return inlineAll(child);
                    }),
                );
        });

        function inlineBackground(node) {
            const background = node.style.getPropertyValue('background');

            if (!background) return Promise.resolve(node);

            return inliner
                .inlineAll(background)
                .then(function (inlined) {
                    node.style.setProperty('background', inlined, node.style.getPropertyPriority('background'));
                })
                .then(function () {
                    return node;
                });
        }
    }
}

export default DomToImage as any as IDomToImage;
